import React, {useCallback} from 'react';
import cx from 'classnames';
import {observer} from 'mobx-react';
import {Button, Flex, Icon, PriorityIcon} from '@priority-software/priority-style-react';
import {FormViewToggler} from '../FormViewToggler';
import {FormPagination} from '../FormPagination';
import {APIActionStatus, formsStore} from '../../../../stores/Form';
import {createTemporarilyRecord} from '../../../../stores/Form/utils';
import {AdvancedSearches} from '../AdvancedSearch/AdvancedSearches';
import {Actions} from './parts/Actions/Actions';
import {callLinkedActions} from '../../actions/screen';
import {Form, FormViewEnum} from '../../../../stores/Form/Form';
import {AutomationsModule} from '../../../Automations/modules/FormRules/FormAutomation.module';
import {createDataTestId} from '../../../../utils/dataTestId';
import {clearFormData} from '../../actions/filters';
import {deleteRecord} from '../../actions/delete';
import {updateFormView} from '../../actions/form';
import {texts} from '../AdvancedSearch/AdvancedSearchDrawer/texts';
import {isCurrentRecordEmpty} from '../Table/utils/isCurrentRecordEmpty';
import {IconWithTooltip} from '../../../../components/Common/IconWithTooltip';
import {handleUnsupportedFeature} from '../../actions/handleUnsupportedFeature';
import {getLocalizedText, texts as oldTexts} from '../../../../constants/TextConst';
import styles from './style.module.scss';

type Props = {
    currentForm: Form;
    openRecordDialog: () => void;
    isFormActive: boolean;
    hideNewEmptyRecord?: () => void;
};

export const FormToolbar = observer((props: Props) => {
    const {currentForm, isFormActive, openRecordDialog, hideNewEmptyRecord} = props;
    const isMultiLineForm = !currentForm.form.OneLine;
    const isParentCollapsed = currentForm.parentForm?.collapsed;
    const isMasterForm = currentForm.level === 0;

    const toggleSearchMode = useCallback(() => {
        currentForm.setSearchModeLevel(currentForm.searchModeLevel ? 0 : 1);
    }, [currentForm]);

    const toggleParentFormView = async () => {
        await callLinkedActions(
            async () => formsStore.currentScreen?.handleFormLevel(currentForm.level),
            async () => {
                currentForm?.parentForm?.toggleViewState(!isParentCollapsed);
                return APIActionStatus.passed;
            }
        );
    };

    const createNewRecord = async () => {
        const currentScreen = formsStore.currentScreen;
        const isActionAllowed = await currentScreen.handleFormLevel(currentForm.level);
        const activeForm = currentScreen.getActiveForm();
        const currentRecord = activeForm.records[activeForm.currentRow];

        if (isActionAllowed) {
            if (currentForm.level === 0) {
                openRecordDialog();
            } else if (!currentRecord.isTemporary || !currentRecord.keys) {
                createTemporarilyRecord(activeForm);
            }
        }
    };

    const isShowClearAllDataButton =
        (currentForm.filters?.length || !currentForm.isEmpty) && !currentForm.parentForm;

    return (
        <Flex
            componentClasses={cx(styles.toolbar, 'pui-form-toolbar-container', {
                [styles.masterFormPadding]:
                    currentForm.level === 0 && currentForm.view === FormViewEnum.form
            })}
            {...createDataTestId('form-toolbar-container', true)}
        >
            <Flex justifyContent="flex-start" componentClasses={styles.leftToolbarPart}>
                {isMultiLineForm ? (
                    <Button
                        componentClasses={cx(styles.btn)}
                        prefixIcon={<Icon icon={PriorityIcon.addSmall} />}
                        size="small"
                        skin="primary"
                        disabled={!!currentForm.searchModeLevel || !isFormActive}
                        onClick={createNewRecord}
                    >
                        {texts.newFormLabel}
                    </Button>
                ) : null}

                {isMultiLineForm ? (
                    <AdvancedSearches currentForm={currentForm} disabled={!isFormActive} />
                ) : null}

                {isShowClearAllDataButton && (
                    <Button
                        skin="text"
                        prefixIcon={<Icon iconColor="primary-700" icon={PriorityIcon.clear} />}
                        componentClasses={styles.clearAllBtn}
                        onClick={() => clearFormData(currentForm)}
                        disabled={!isFormActive}
                    >
                        {texts.clearAll}
                    </Button>
                )}

                {isMasterForm || (isFormActive && isMultiLineForm) ? (
                    <FormPagination
                        form={currentForm}
                        componentClasses={styles.pagination}
                        formView={currentForm.view}
                        isActiveForm={isFormActive}
                    />
                ) : null}
            </Flex>

            <Flex justifyContent="flex-end" componentClasses={styles.rightToolbarPart}>
                {isMultiLineForm ? (
                    <Actions
                        actions={currentForm?.actions || []}
                        disabled={!isFormActive || currentForm?.currentRecord?.isEmpty()}
                    />
                ) : null}

                <div className={cx(styles.divider, styles.marginXmd)} />

                {!formsStore.currentScreen.isRecordDialog && (
                    <AutomationsModule
                        form={currentForm}
                        componentClasses={styles.marginXsm}
                        disabled={!isFormActive}
                    />
                )}

                {currentForm.ename !== 'MAILBOXSEND' ? (
                    <>
                        <div className={cx(styles.divider)} />
                        <Button
                            componentClasses="print-label"
                            prefixIcon={PriorityIcon.printLabels}
                            skin="text"
                            onClick={() => handleUnsupportedFeature('Notifications')}
                        >
                            {getLocalizedText(oldTexts.printLabel)}
                        </Button>
                    </>
                ) : null}

                {isMultiLineForm ? (
                    <>
                        <div className={cx(styles.divider, styles.marginXmd)} />

                        <FormViewToggler
                            componentClasses={styles.marginXmd}
                            activeView={currentForm.view}
                            onViewChange={view => updateFormView(currentForm, view)}
                            isDisabled={!isFormActive}
                        />
                        {!formsStore.currentScreen.isRecordDialog && (
                            <IconWithTooltip
                                tooltip={texts.searchModeTooltip}
                                disabled={!isFormActive}
                                componentClasses={cx(styles.marginXmd, styles.btn, styles.f11icon, {
                                    [styles.active]: currentForm.searchModeLevel,
                                    [styles.disabled]: !isFormActive
                                })}
                                icon={PriorityIcon.F11}
                                onClick={toggleSearchMode}
                                position="bottom"
                            />
                        )}
                    </>
                ) : null}

                <div className={cx(styles.divider, styles.marginXmd)} />

                <Flex inline style={{gap: 8}}>
                    <IconWithTooltip
                        tooltip={texts.deleteRecordTooltip}
                        icon={PriorityIcon.delete}
                        disabled={isCurrentRecordEmpty(currentForm) || !isFormActive}
                        onClick={() => deleteRecord(currentForm, hideNewEmptyRecord)}
                        position="bottom"
                    />

                    <IconWithTooltip
                        tooltip={texts.shareTooltip}
                        icon={PriorityIcon.iconShare}
                        position="bottom"
                        onClick={() => handleUnsupportedFeature('Share')}
                        disabled={!isFormActive}
                    />

                    <IconWithTooltip
                        position="bottom"
                        tooltip={texts.notificationTooltip}
                        icon={PriorityIcon.notification}
                        onClick={() => handleUnsupportedFeature('Notifications')}
                        disabled={!isFormActive}
                    />

                    {currentForm.level === 1 && (
                        <IconWithTooltip
                            position="bottom"
                            componentClasses={styles.expandBtn}
                            icon={isParentCollapsed ? PriorityIcon.shrink : PriorityIcon.expand}
                            onClick={toggleParentFormView}
                        />
                    )}
                </Flex>
            </Flex>
        </Flex>
    );
});
